/**
 * Types, parser and label helpers for the `time_correction` change-request
 * family (#1979): an employee asks to correct the worked time of one day. The
 * server builds both spans; the inbox shows them side by side with the
 * difference. Re-exported from `./changeRequest`.
 */

import type { I18nLike } from './changeRequest';

export const TIME_CORRECTION_MODES = ['shift', 'tracking'] as const;
export type TimeCorrectionMode = (typeof TIME_CORRECTION_MODES)[number];

/** One side of a correction; start and end are absent when the day counted nothing. */
export interface TimeCorrectionSpan {
  start?: string;
  end?: string;
  breakMinutes: number;
  netMinutes: number;
}

/** The `time_correction` executor body, camelCase like the backend spec. */
export interface TimeCorrectionBody {
  employeeId: string;
  employeeName?: string;
  /** YYYY-MM-DD */
  date: string;
  shiftId?: string;
  mode: TimeCorrectionMode;
  old: TimeCorrectionSpan;
  new: TimeCorrectionSpan;
  deltaMinutes: number;
  reason: string;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function isSpan(v: unknown): v is TimeCorrectionSpan {
  return (
    isRecord(v) &&
    (v.start === undefined || typeof v.start === 'string') &&
    (v.end === undefined || typeof v.end === 'string') &&
    typeof v.breakMinutes === 'number' &&
    typeof v.netMinutes === 'number'
  );
}

/** The body when it has the pinned shape, else null (rendered as the raw payload). */
export function parseTimeCorrectionBody(body: unknown): TimeCorrectionBody | null {
  if (!isRecord(body)) return null;
  if (typeof body.employeeId !== 'string' || typeof body.date !== 'string') return null;
  if (body.mode !== 'shift' && body.mode !== 'tracking') return null;
  if (!isSpan(body.old) || !isSpan(body.new)) return null;
  if (typeof body.deltaMinutes !== 'number' || typeof body.reason !== 'string') return null;
  return body as unknown as TimeCorrectionBody;
}

/** "8:30 h" from minutes. */
export function formatCorrectionMinutes(minutes: number): string {
  const abs = Math.abs(Math.round(minutes));
  return `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')} h`;
}

/** "+1:00 h" / "-0:30 h". */
export function formatCorrectionDelta(minutes: number): string {
  return `${minutes < 0 ? '-' : '+'}${formatCorrectionMinutes(minutes)}`;
}

/** "08:00 - 16:30", or "-" when the side counted nothing. */
export function formatCorrectionSpan(span: TimeCorrectionSpan): string {
  if (!span.start && !span.end) return '-';
  return `${span.start ?? '...'} - ${span.end ?? '...'}`;
}

/** What the rule at submit counted: the shifts (tracking off) or the tracked time. */
export function getTimeCorrectionModeLabel(i18n: I18nLike, mode: string | undefined): string {
  switch (mode) {
    case 'shift':
      return i18n._({ id: 'changeRequest.timeCorrection.mode.shift', message: 'Shift is working time' });
    case 'tracking':
      return i18n._({ id: 'changeRequest.timeCorrection.mode.tracking', message: 'Tracked time' });
    default:
      return mode ?? '';
  }
}
